import { flip, offset } from '@floating-ui/react';
import { classNames } from '@panwds/tailwindcss';
import escapeStringRegexp from 'escape-string-regexp';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { getAutoPlacement, usePopper } from '../../utils/index.js';
import { noop } from '../../utils/noop.js';
import { MenuIcon } from '../Icon/index.js';
import { Input } from '../Input/Input.js';
import { List, ListItem } from '../List/index.js';
import { Portal } from '../Portal/index.js';
/**
 * Combo box combines an input with a list menu.
 * It enables users to select a predefined value from the list or type their own value in the input field.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { ComboBox } from "@panwds/react-ui";
 * ```
 */
export const ComboBox = forwardRef(({ size = 'md', className, dataTestId = 'default', options, numberOfVisibleItemsWithoutScroll = 10, onChange = noop, defaultPlacement = 'bottom-start', enableHighlight = true, showSuffixIcon = true, value, defaultValue, enableInitialFocus, ...passThrough }, userRef) => {
    /**
     * State variables:
     * - inputValue: The current value of the input field.
     * - listOpen: A boolean that tracks whether the options list is currently open.
     * - matchedOptions: An array of options that match the current input value after filtering.
     * - selectedIndex: The index of the currently selected option in the dropdown list.
     * - listRef: A ref to the dropdown list element for scrolling purposes.
     */
    const [internalInputValue, setInternalInputValue] = useState(defaultValue ?? '');
    const inputValue = value !== undefined ? value : internalInputValue;
    const [listOpen, setListOpen] = useState(false);
    const [matchedOptions, setMatchedOptions] = React.useState(options || []);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef(null);
    /**
     * Filter options based on inputValue
     * This effect runs whenever inputValue or options change
     */
    useEffect(() => {
        const matchedOptions = options?.filter((option) => option?.label?.toLowerCase()?.includes(inputValue?.toLowerCase())) || [];
        setMatchedOptions(matchedOptions);
    }, [inputValue, options]);
    const setInputValue = useCallback((newValue) => {
        if (value === undefined) {
            setInternalInputValue(newValue ?? '');
        }
    }, [value]);
    const { placement, autoPlacement } = getAutoPlacement(defaultPlacement);
    /**
     * Set up popper for dropdown positioning
     * This uses the usePopper hook to handle the positioning of the dropdown
     */
    const { refs, floatingStyles, getFloatingProps } = usePopper({
        placement,
        strategy: 'fixed',
        middleware: [
            autoPlacement,
            offset(2),
            {
                name: 'sameWidth',
                options: {
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    enabled: true
                },
                fn: (state) => {
                    // todo: potential bug
                    state.elements.floating.style.width = `${state.rects.reference.width}px`;
                    return {};
                }
            },
            flip()
        ]
    });
    /**
     * Scroll the selected option into view when the dropdown is open
     * This effect runs whenever selectedIndex or listOpen change
     */
    useEffect(() => {
        if (listOpen && listRef.current && selectedIndex >= 0) {
            const selectedElement = listRef.current.children[selectedIndex];
            selectedElement?.scrollIntoView?.({ block: 'nearest' });
        }
    }, [selectedIndex, listOpen]);
    /**
     * Handle the input change event
     * @param event - The input change event object
     */
    const handleInputChange = useCallback((event) => {
        const newValue = event.target.value;
        setListOpen(true);
        setInputValue(newValue);
        // Call the provided onChange handler if it exists
        onChange?.({ inputValue: newValue });
    }, [value]);
    /**
     * Handle keyboard navigation for the combo box
     * @param event - The keyboard event object
     */
    const handleKeyDown = useCallback((event) => {
        if (listOpen) {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setSelectedIndex((prevIndex) => (prevIndex < matchedOptions.length - 1 ? prevIndex + 1 : prevIndex));
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (selectedIndex >= 0 && selectedIndex < matchedOptions.length) {
                        const selectedOption = matchedOptions[selectedIndex];
                        setInputValue(selectedOption?.label);
                        onChange?.({ inputValue: selectedOption?.label, selectedOption: selectedOption });
                        setListOpen(false);
                        setSelectedIndex(0);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    setListOpen(false);
                    break;
            }
        }
    }, [listOpen, matchedOptions, selectedIndex, value]);
    /**
     * Highlight the matched part of the option text
     * @param text - The text to be highlighted
     * @returns JSX element with the matching part of the text highlighted
     */
    const highlightMatch = (text) => {
        if (!inputValue)
            return text;
        const escapedInputValue = escapeStringRegexp(inputValue);
        const parts = text.split(new RegExp(`(${escapedInputValue})`, 'gi'));
        return (React.createElement(React.Fragment, null, parts.map((part, index) => part.toLowerCase() === inputValue.toLowerCase() ? (React.createElement("span", { key: index, className: "twds-text-gray-900 dark:twds-text-gray-400 twds-font-semibold" }, part)) : (React.createElement("span", { key: index, className: "twds-text-gray-600 dark:twds-text-dark-bg" }, part)))));
    };
    // Calculate maxHeight based on numberOfVisibleItemsWithoutScroll and size
    const getMaxHeight = useCallback(() => {
        const itemHeight = size === 'sm' ? 24 : size === 'md' ? 32 : 40; // height in pixels for each size
        return `${Math.min(numberOfVisibleItemsWithoutScroll, matchedOptions.length) * (itemHeight + 2)}px`;
    }, [size, numberOfVisibleItemsWithoutScroll, matchedOptions.length]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Input, { addClassName: className?.input, type: "string", dataTestId: `${dataTestId}-combo-box`, ref: userRef, size: size, onFocus: () => setListOpen(true), onBlur: () => setListOpen(false), onChange: handleInputChange, onKeyDown: handleKeyDown, value: inputValue, autoFocus: enableInitialFocus, suffix: showSuffixIcon && (React.createElement("div", { className: "twds-pr-2" },
                React.createElement(MenuIcon, { isOpen: matchedOptions.length > 0 && listOpen, size: size === 'sm' ? 'xs' : 'sm' }))), containerRef: refs.setReference, ...passThrough }),
        matchedOptions.length > 0 && (React.createElement(Portal, null,
            React.createElement("div", { className: classNames('twds-z-30', className?.portal), ref: refs.setFloating, style: floatingStyles, ...getFloatingProps(), "data-testid": "portal-div" },
                React.createElement(List, { isOpen: listOpen, ref: listRef, style: { maxHeight: getMaxHeight() }, addClassName: className?.list }, matchedOptions.map((option, index) => (React.createElement(ListItem, { key: option?.id, onClick: (event) => {
                        event.stopPropagation();
                        setInputValue(option?.label);
                        onChange?.({ inputValue: option?.label, selectedOption: option });
                        setListOpen(false);
                    }, addClassName: classNames(index === selectedIndex && 'twds-bg-blue-100 dark:twds-bg-gray-800', size === 'sm' && 'twds-text-xs twds-h-6 twds-py-1 twds-px-2', size === 'md' && 'twds-text-xs twds-h-8 twds-py-2 twds-px-4', size === 'lg' && 'twds-text-sm twds-h-10 twds-py-[10px] twds-px-4', className?.listItem) }, enableHighlight ? highlightMatch(option?.label) : option?.label)))))))));
});
//# sourceMappingURL=combo_box.js.map